// @flow
import * as React from 'react';
import { connect } from 'react-redux';
import Modal from '../common/modal';
import { formActions, mergeActionsToProps } from 'redux-pure-form';

import type { MapStateToProps } from 'react-redux';

import Indication from '../ui/indication.js';
import LoadingIndicator from '../ui/loading-indicator.js';
import Breadcrumbs from '../breadcrumbs/breadcrumbs.js';
import Select, { type Option } from '../form/select.js';
import GroupedSelect from '../form/grouped-select.js';
import Datepicker from '../common/datepicker/datepicker';
import { RowResource } from './invoice.row-resource';

import {
  invoiceSelector,
  invoiceResourcesSelector,
  invoiceResourcesListSelector,
  invoiceProductsSelector,
  invoiceProductsListSelector,
  invoiceProductsOptionsSelector,
  invoiceResourceOptionsSelector
} from './invoice.selectors.js';

import TransactionsTable from '../transaction/transactions.table.js';

import { eventSelector } from '../event/event.selectors.js';

import {
  transactionsSelector,
  transactionsListSelector
} from '../transaction/transaction.selectors.js';

import {
  organizationByEventSelector,
  organizationSelector,
  organizationsSelector
} from '../organization/organization.selectors.js';

import { productsSelector } from '../products/products.selectors.js';
import { resourcesSelector } from '../resource/resource.selectors.js';
import { usersSelector, userSelector, payerSelector } from '../user/user.selectors.js';
import Event from '../event/event.model.js';
import Organization from '../organization/organization.model.js';

import { selectUIModal, selectUIMessages, selectUIProgress } from '../ui/ui.helpers.js';
import { setModal, setMessages } from '../ui/ui.actions.js';
import { ADD_PRODUCT, ADD_RESOURCE, errorsInvoice, errorKeys, FIELD_ID } from './invoice.const.js';

import { errorMessages } from '../payments/payments.const.js';

import {
  getPrice,
  deleteProduct,
  deleteResource,
  deleteCounter,
  addInvoice,
  saveInvoice,
  deleteInvoice
} from './invoice.actions.js';
import {
  DECIMAL_NEGATIVE_REGEXP,
  DECIMAL_REGEXP,
  DECIMAL_REGEXP_EXTENDED,
  DECIMAL_REGEXP_EXTENDED_MAX
} from '../utils/validation';

import { formatDate, getTimestamp, newDate } from '../calendar/calendar.model';

class InvoiceScreen extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.timer = null;
    this.setGetPriceTimer = this.setGetPriceTimer.bind(this);
    this.clearTimer = this.clearTimer.bind(this);

    this.onTableChange = this.onTableChange.bind(this);
    this.onBlur = this.onBlur.bind(this);
    this.handleDateChange = this.handleDateChange.bind(this);

    this.state = {
      additionalProduct: undefined,
      additionalResource: undefined,
      transactionId: '',
      errors: errorsInvoice,
      hasError: false
    };

    this.onAddProductOpen = this.props.setModal.bind(this, true, ADD_PRODUCT);
    this.onAddProductClose = this.props.setModal.bind(this, false, ADD_PRODUCT);
    this.onAddProduct = this.onAddAdditional.bind(this, 'additionalProduct');
    this.onSelectProduct = this.onSelectAdditional.bind(this, 'additionalProduct');
    this.onDeleteProduct = this.onDeleteAdditional.bind(this, 'additionalProduct');

    this.onAddResourceOpen = this.props.setModal.bind(this, true, ADD_RESOURCE);
    this.onAddResourceClose = this.props.setModal.bind(this, false, ADD_RESOURCE);
    this.onAddResource = this.onAddAdditional.bind(this, 'additionalResource');
    this.onSelectResource = this.onSelectAdditional.bind(this, 'additionalResource');
    this.onDeleteResource = this.onDeleteAdditional.bind(this, 'additionalResource');
    this.onDeleteCounter = this.onDeleteAdditional.bind(this, 'additionalCounter');

    this.onCreateInvoice = this.onCreateInvoice.bind(this);
    this.onSaveInvoice = this.onSaveInvoice.bind(this);
    this.onDeleteInvoice = this.onDeleteInvoice.bind(this);
  }

  componentWillUnmount() {
    this.clearTimer();
  }

  shouldComponentUpdate(nextProps: Props, nextState: State, nextContext: any): boolean {
    // TODO Add checks for props so we are not rebuilding element 5 times on each keyup
    return true;
  }

  clearTimer: Function;
  clearTimer() {
    if (this.timer) {
      window.clearTimeout(this.timer);
    }
    if (this.timerChangeDiscount) {
      window.clearTimeout(this.timerChangeDiscount);
    }
  }

  setGetPriceTimer: Function;
  setGetPriceTimer() {
    this.timer = window.setTimeout(this.props.getPrice, 5000);
  }

  setError: Function;
  setError(name: string, bool: boolean) {
    if (this.state.errors[name] !== bool) {
      const errors = {
        ...this.state.errors,
        [name]: bool
      };

      this.setState({ errors });

      const messages = errorKeys.reduce((acc, err) => {
        if (errors[err])
          return acc.concat({
            type: 'warning',
            text: errorMessages[err],
            timeout: Infinity
          });

        return acc;
      }, []);

      this.props.setMessages(messages);

      this.setState({ hasError: Boolean(messages.length) });
    }
  }

  isValid(key, value) {
    switch (key) {
      case FIELD_ID.QTY:
      case FIELD_ID.DISCOUNT:
      case FIELD_ID.RATE:
        return DECIMAL_REGEXP_EXTENDED_MAX.test(value);
      case FIELD_ID.SALES_TAX:
        return DECIMAL_REGEXP_EXTENDED.test(value) && parseInt(value, 10) < 101;
      case FIELD_ID.DISCOUNT_PERCENTAGE:
        return DECIMAL_REGEXP_EXTENDED.test(value) && parseInt(value, 10) < 101;
      case FIELD_ID.INVOICE_NUMBER:
        return Boolean(value.trim());
      default:
        return true;
    }
  }

  onTableChange: Function;
  onTableChange(e) {
    // Using custom validation for let user to enter float numbers with comma
    this.clearTimer();

    const { name, id, checked, value } = e.currentTarget;
    const isValid = this.isValid(id, value);

    if (isValid) {
      this.setGetPriceTimer();
    }

    this.setError(id, !isValid);

    return this.props.fieldAttrs.onChange(
      name,
      id === FIELD_ID.TAXABLE ? (checked ? 1 : 0) : value
    );
  }

  onBlur: Function;
  onBlur(e) {
    this.clearTimer();
    const { name, id, checked, value } = e.currentTarget;

    const isValid = this.isValid(id, value);

    if (isValid) {
      this.props.getPrice();
    }

    this.setError(id, !isValid);

    return this.props.fieldAttrs.onChange(
      name,
      id === FIELD_ID.TAXABLE ? (checked ? 1 : 0) : value
    );
  }

  onCreateInvoice() {
    return this.props.addInvoice();
  }

  onSaveInvoice() {
    return this.props.saveInvoice();
  }

  onDeleteInvoice() {
    return confirm('Are you sure you want to delete invoice?') ? this.props.deleteInvoice() : false;
  }

  onDeleteAdditional(name, e) {
    let confirmText = '';
    switch (name) {
      case 'additionalResource':
      case 'additionalCounter':
        confirmText = 'Resource';
        break;
      case 'additionalProduct':
        confirmText = 'Serveice/Product';
        break;
    }
    const { id, resourceid } = e.currentTarget.dataset;
    const canDelete = confirm(`Are you sure you want to delete ${confirmText} from this invoice?`);
    if (canDelete) {
      switch (name) {
        case 'additionalCounter':
          return this.props.deleteCounter(id, resourceid);
        case 'additionalResource':
          return this.props.deleteResource(id);
        case 'additionalProduct':
          return this.props.deleteProduct(id);
      }
    }
  }

  onSelectAdditional(name, value: $ElementType<Option, 'value'>) {
    return this.setState({
      [name]: value
    });
  }

  onAddAdditional(name, e) {
    e.preventDefault();

    const additionalId = this.state[name]?.value;

    if (this.props.progress || !additionalId) return false;

    const isAdditionalProduct = name === 'additionalProduct';

    if (isAdditionalProduct) {
      this.props.fieldAttrs.onChange('invoice.products', {
        ...this.props.invoice.products,
        [additionalId]: this.props.products[additionalId]
      });
    } else {
      this.props.fieldAttrs.onChange('invoice.resources', {
        ...this.props.invoice.resources,
        [additionalId]: this.props.resources[additionalId]
      });
    }

    this.onSelectAdditional(name, undefined);

    this.props.getPrice();

    return isAdditionalProduct ? this.onAddProductClose() : this.onAddResourceClose();
  }

  handleDateChange(val) {
    return this.props.fieldAttrs.onChange(
      'invoice.userCreateTimestamp',
      formatDate(new Date(val).getTime() / 1000, 0, 'Y-m-d', false, false)
    );
  }

  timer: ?number;

  render() {
    const {
      URL,
      edit,
      view,
      create,
      addProduct,
      addResource,
      messages,
      progress,
      invoice,
      event,
      users,
      payer,
      user,
      organization,
      orgEvent,
      invoiceResources,
      invoiceResourcesList,
      invoiceProducts,
      invoiceProductsList,
      invoiceProductsOptions,
      invoiceResourceOptions,
      accessgroups
    } = this.props;

    if (!invoice) return null;

    const disabled =
      progress ||
      this.state.hasError ||
      !(invoiceProductsList.length || invoiceResourcesList.length);

    return (
      <div>
        <Breadcrumbs />

        <Indication messages={messages} closable className="message-indicator" />

        <div className="invoice-title-container">
          <div className="invoice-title-block">
            <h3 className="invoice-title-group">
              {create ? (
                <span className="invoice-create-title invoice-text">Create Invoice #</span>
              ) : (
                <span className="invoice-title invoice-text">Invoice #</span>
              )}

              {!view ? (
                <input
                  type="text"
                  maxLength={255}
                  id={FIELD_ID.INVOICE_NUMBER}
                  name="invoice.number"
                  value={invoice.number}
                  onChange={this.onTableChange}
                  className="form-control invoice-title-input"
                  required
                />
              ) : (
                <span className="invoice-text">{invoice.number}</span>
              )}

              {view &&
              invoice.id &&
              (orgEvent.unique || organization.unique) &&
              Organization.orgAccess ? (
                <a
                  className="btn btn-default edit-btn"
                  href={`${window.serverData._uri}/org/${orgEvent.unique ||
                    organization.unique}/invoices/edit/${invoice.id}/`}
                >
                  Edit
                </a>
              ) : null}
            </h3>

            <div className="invoice-title-datepicker-wrap">
              <span className="invoive-title-datepicker-text">Invoice Date:</span>
              {view ? (
                <span className="form-control-static">
                  {formatDate(invoice.userCreateTimestamp, 0, 'd M Y')}
                </span>
              ) : (
                <Datepicker
                  id="userCreateTimestamp"
                  className="form-control inner-datepicker input-sm"
                  onChange={this.handleDateChange}
                  selected={newDate(getTimestamp(invoice.userCreateTimestamp))}
                  dateFormat={user.settings.date}
                  required
                />
              )}
            </div>
          </div>
          <div className="invoice-title-progress-wrap">
            {progress ? <LoadingIndicator /> : null}
          </div>
        </div>

        <Modal
          isOpen={addProduct}
          onClose={this.onAddProductClose}
          className="row smart-dialog-modern"
          scrollable={false}
          header={<div className="smart-dialog-header">Add Service/Product</div>}
          footer={
            <div className="col-md-12 buttons">
              <label
                htmlFor="btn-submit-product"
                disabled={progress || !this.state.additionalProduct}
                className="btn btn-success pull-right"
              >
                Add Service/Product
              </label>
            </div>
          }
        >
          <form onSubmit={this.onAddProduct}>
            <div className="spaced-group">
              <div className="modal-search-label">Select Service/Product</div>
              <div className="resource-select-block">
                <Select
                  pretty
                  searchable
                  clearable
                  disabled={progress}
                  placeholder="Select or search Service/Product"
                  options={invoiceProductsOptions}
                  value={this.state.additionalProduct}
                  onChange={this.onSelectProduct}
                />
              </div>
              <div className="block-help search-help">
                Quantity and discount can be changed after adding the item to the list
              </div>
            </div>

            <input type="submit" className="hidden" id="btn-submit-product" />
          </form>
        </Modal>

        <Modal
          isOpen={addResource}
          onClose={this.onAddResourceClose}
          className="row smart-dialog-modern"
          scrollable={false}
          header={<div className="smart-dialog-header">Add Resource</div>}
          footer={
            <div className="col-md-12 buttons">
              <label
                htmlFor="btn-submit-resource"
                disabled={progress || !this.state.additionalResource}
                className="btn btn-success pull-right"
              >
                Add Resource
              </label>
            </div>
          }
        >
          <form onSubmit={this.onAddResource}>
            <div className="spaced-group">
              <div className="modal-search-label">Select Resource</div>
              <div className="resource-select-block">
                <GroupedSelect
                  searchable
                  clearable
                  simpleValue
                  disabled={progress}
                  className="resources-select"
                  placeholder="Search Resource"
                  options={invoiceResourceOptions}
                  value={this.state.additionalResource}
                  onChange={this.onSelectResource}
                />
              </div>
              <div className="block-help search-help">
                Quantity and discount can be changed after adding the item to the list
              </div>
            </div>

            <input type="submit" className="hidden" id="btn-submit-resource" />
          </form>
        </Modal>

        <div className="invoice-container">
          <div className="invoice-info">
            <div className="invoice-resources">
              {invoiceResourcesList.length ? (
                <div className="panel panel-default">
                  <div className="table-responsive">
                    <table className="table table-sm table-hover table-panel">
                      <thead>
                        <tr>
                          <th className="text-left col-md-4">Resources</th>
                          <th className="text-right col-md-1">QTY</th>
                          <th className="text-right col-md-1">Rate</th>
                          <th className="text-right col-md-1">Amount</th>
                          <th className="text-right col-md-1">
                            <span className="persent-table-heading">Discount, %</span>
                          </th>
                          <th className="text-right col-md-1">
                            <span className="persent-table-heading">Discount</span>
                          </th>
                          <th className="text-right col-md-1">Subtotal</th>
                          <th className="text-right col-md-1">Taxable</th>
                          <th className="text-right col-md-1">
                            <span className="persent-table-heading">Tax Rate, %</span>
                          </th>
                          <th className="text-right col-md-1">Tax</th>
                          <th className="text-right col-md-1">Total</th>
                          {view ? null : <th className="text-right col-md-1">Action</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {invoiceResourcesList.map(id => {
                          const invoiceResource = invoiceResources[id];
                          const countersList = invoiceResource.counters
                            ? Object.keys(invoiceResource.counters)
                            : [];
                          const isCountersEngine =
                            invoiceResource.typeName === 'Aircraft' &&
                            invoiceResource.payStatus === 'perhour' &&
                            countersList.length > 0 &&
                            countersList.some(
                              counterId => invoiceResource.counters[counterId].enableRate === '1'
                            );
                          if (isCountersEngine) {
                            return countersList.map(counterId => {
                              const counter = invoiceResource.counters[counterId];
                              if (counter.enableRate === '0') {
                                return null;
                              }
                              return (
                                <RowResource
                                  key={`res-${id}-${counterId}`}
                                  typeName={invoiceResource.typeName}
                                  name={invoiceResources[id].name}
                                  counterName={counter.name}
                                  view={view}
                                  progress={progress}
                                  onTableChange={this.onTableChange}
                                  onBlur={this.onBlur}
                                  onDelete={this.onDeleteCounter}
                                  total={counter.total || ''}
                                  id={counterId}
                                  resourceId={id}
                                  qty={counter.diff || ''}
                                  qtyInputName={`invoice.resources.${id}.counters.${counterId}.diff`}
                                  rate={counter.rate || ''}
                                  rateInputName={`invoice.resources.${id}.counters.${counterId}.rate`}
                                  discount={counter.discount || ''}
                                  discountInputName={`invoice.resources.${id}.counters.${counterId}.discount`}
                                  subtotal={counter.subtotal || ''}
                                  salesTax={counter.salesTax || ''}
                                  salesTaxInputName={`invoice.resources.${id}.counters.${counterId}.salesTax`}
                                  totalSalesTax={counter.totalSalesTax || ''}
                                  taxable={counter.taxable || ''}
                                  taxableTaxInputName={`invoice.resources.${id}.counters.${counterId}.taxable`}
                                />
                              );
                            });
                          }
                          return (
                            <RowResource
                              key={`res-${id}`}
                              typeName={invoiceResources[id].typeName}
                              name={invoiceResources[id].name}
                              counterName={null}
                              view={view}
                              progress={progress}
                              onTableChange={this.onTableChange}
                              onDelete={this.onDeleteResource}
                              onBlur={this.onBlur}
                              total={invoiceResources[id].total}
                              id={id}
                              resourceId={null}
                              qty={invoiceResources[id].timeOfUse}
                              qtyInputName={`invoice.resources.${id}.timeOfUse`}
                              rate={invoiceResources[id].rate}
                              rateInputName={`invoice.resources.${id}.rate`}
                              discount={invoiceResources[id].discount}
                              discountInputName={`invoice.resources.${id}.discount`}
                              subtotal={invoiceResources[id].subtotal}
                              salesTax={invoiceResources[id].salesTax}
                              salesTaxInputName={`invoice.resources.${id}.salesTax`}
                              totalSalesTax={invoiceResources[id].totalSalesTax}
                              taxable={invoiceResources[id].taxable}
                              taxableTaxInputName={`invoice.resources.${id}.taxable`}
                            />
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : !view ? (
                <div className="panel panel-default no-panel-block">
                  <h4 className="text-center no-panel-text">There aren't any Resources yet.</h4>
                </div>
              ) : null}

              {!view ? (
                <button
                  type="button"
                  disabled={progress}
                  onClick={this.onAddResourceOpen}
                  className="btn invoice-btn"
                >
                  <span className="icon plus-circled" /> Add Resource
                </button>
              ) : null}
            </div>

            <div className="invoice-products">
              {invoiceProductsList.length ? (
                <div className="panel panel-default products-table">
                  <div className="table-responsive">
                    <table className="table table-sm table-hover table-panel">
                      <thead>
                        <tr>
                          <th className="text-left col-md-4">Products</th>
                          <th className="text-right col-md-1">QTY</th>
                          <th className="text-right col-md-1">Rate</th>
                          <th className="text-right col-md-1">Amount</th>
                          <th className="text-right col-md-1">
                            <span className="persent-table-heading">Discount, %</span>
                          </th>
                          <th className="text-right col-md-1">
                            <span className="persent-table-heading">Discount</span>
                          </th>
                          <th className="text-right col-md-1">Subtotal</th>
                          <th className="text-right col-md-1">Taxable</th>
                          <th className="text-right col-md-1">
                            <span className="persent-table-heading">Tax Rate, %</span>
                          </th>
                          <th className="text-right col-md-1">Tax</th>
                          <th className="text-right col-md-1">Total</th>
                          {view ? null : <th className="text-right col-md-1">Action</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {invoiceProductsList.map(id => (
                          <RowResource
                            key={`products-${id}`}
                            typeName={invoiceProducts[id].typeName}
                            name={invoiceProducts[id].name}
                            counterName={null}
                            view={view}
                            progress={progress}
                            onTableChange={this.onTableChange}
                            onDelete={this.onDeleteProduct}
                            onBlur={this.onBlur}
                            total={invoiceProducts[id].total}
                            id={id}
                            resourceId={null}
                            qty={invoiceProducts[id].amount}
                            qtyInputName={`invoice.products.${id}.amount`}
                            rate={invoiceProducts[id].rate}
                            rateInputName={`invoice.products.${id}.rate`}
                            discount={invoiceProducts[id].discount}
                            discountInputName={`invoice.products.${id}.discount`}
                            subtotal={invoiceProducts[id].subtotal}
                            salesTax={invoiceProducts[id].salesTax}
                            salesTaxInputName={`invoice.products.${id}.salesTax`}
                            totalSalesTax={invoiceProducts[id].totalSalesTax}
                            taxable={invoiceProducts[id].taxable}
                            taxableTaxInputName={`invoice.products.${id}.taxable`}
                          />
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : !view ? (
                <div className="panel panel-default no-panel-block">
                  <h4 className="text-center no-panel-text">
                    There aren't any Services/Products yet.
                  </h4>
                </div>
              ) : null}

              {!view ? (
                <button
                  type="button"
                  disabled={progress}
                  onClick={this.onAddProductOpen}
                  className="btn invoice-btn"
                >
                  <span className="icon plus-circled" /> Add Service/Product
                </button>
              ) : null}

              {view ? <TransactionsTable invoiceView /> : null}
            </div>

            <div className={`total-line ${!view ? 'wide' : ''}`} />
            <div className="total-rate-block">
              <div className="total-rate-names">
                <span className="total-rate-item">Original Sum</span>
                <span className="total-rate-item">Subtotal</span>
                <span className="total-rate-item">Discount</span>
                <span className="total-rate-item">Tax</span>
                <span className="total-rate-item total-rate total-rate-item-taxes total-rate-item-taxes-line">
                  Total
                </span>
                <span className="total-rate-item total-rate">Amount Paid</span>
                <span className="total-rate-item total-rate">Balance Due</span>
              </div>
              <div className="total-rate-values">
                <span className="total-rate-item">
                  {invoice.originalTotalPrice
                    ? `$${parseFloat(invoice.originalTotalPrice).toFixed(2)}`
                    : '$0.00'}
                </span>
                <span className="total-rate-item">
                  {invoice.subtotal ? `$${parseFloat(invoice.subtotal).toFixed(2)}` : '$0.00'}
                </span>
                <span className="total-rate-item">
                  {invoice.totalDiscount
                    ? `$${parseFloat(invoice.totalDiscount).toFixed(2)}`
                    : '$0.00'}
                </span>
                <span className="total-rate-item">
                  {invoice.totalSalesTax
                    ? `$${parseFloat(invoice.totalSalesTax).toFixed(2)}`
                    : '$0.00'}
                </span>
                <span className="total-rate-item total-rate total-rate-item-taxes">
                  {invoice.totalPrice ? `$${parseFloat(invoice.totalPrice).toFixed(2)}` : '$0.00'}
                </span>
                <span className="total-rate-item total-rate">
                  {invoice.paidAmount ? `$${parseFloat(invoice.paidAmount).toFixed(2)}` : '$0.00'}
                </span>
                <span className="total-rate-item total-rate">
                  {invoice.balance ? `$${parseFloat(invoice.balance).toFixed(2)}` : '$0.00'}
                </span>
              </div>
            </div>

            {create ? (
              <button
                type="button"
                disabled={disabled}
                onClick={this.onCreateInvoice}
                className="btn btn-success large-btn pull-right"
              >
                Create Invoice
              </button>
            ) : null}

            {edit ? (
              <div className="save-btn-group">
                <div className="send-email">
                  <input
                    id="sendEmail"
                    type="checkbox"
                    value="1"
                    name="invoice.sendEmail"
                    checked={invoice.sendEmail}
                    onChange={e =>
                      this.props.fieldAttrs.onChange(e.currentTarget.name, e.currentTarget.checked)
                    }
                  />
                  <label htmlFor="sendEmail" className="send-email-label">
                    Send edited invoice
                  </label>
                </div>

                <button
                  type="button"
                  disabled={disabled}
                  onClick={this.onSaveInvoice}
                  className="btn btn-success large-btn pull-right"
                >
                  Save Invoice
                </button>
              </div>
            ) : null}
          </div>

          <div className="event-info-block">
            <div className="event-info-block-inner">
              <div className="paid-status">
                {view ? (
                  <div className="payment-status">
                    <span>
                      Payment Status:{' '}
                      <span className={`payment-status-text ${invoice.paidStatus}`}>
                        {invoice.paidStatus}
                      </span>
                    </span>
                    {invoice.paidStatus === 'unpaid' &&
                    (orgEvent.unique || organization.unique) &&
                    invoice.id ? (
                      <a
                        className="btn btn-success pay-btn pull-right"
                        href={
                          URL.org
                            ? `${window.serverData._uri}/org/${orgEvent.unique ||
                                organization.unique}/invoices/pay/${invoice.payerId}/?=${
                                invoice.id
                              }`
                            : `/invoices/pay/${invoice.organizationId}/?=${invoice.id}`
                        }
                      >
                        Pay
                      </a>
                    ) : null}
                  </div>
                ) : null}
              </div>

              {event.id ? (
                <div className="event-info">
                  <div className="event-info-group-block">
                    <div className="event-info-group">
                      <div className="block-help no-padding">Organization:</div>
                      <div className="block-info">{orgEvent.name}</div>
                    </div>

                    {invoice.createdDateTime ? (
                      <div className="event-info-group">
                        <div className="block-help">Invoice Date:</div>
                        <div className="block-info">
                          {formatDate(invoice.userCreateTimestamp, 0, 'd M Y')}
                        </div>
                      </div>
                    ) : null}

                    {orgEvent.unique && event.link ? (
                      <div className="event-info-group event-info-group-flex-statrt">
                        <div className="block-info">
                          {accessgroups.calendarView ? (
                            <a
                              href={`${window.serverData._uri}/org/${orgEvent.unique}${event.link}`}
                            >
                              Event <span className="icon external-link" />
                            </a>
                          ) : (
                            <span>Event:</span>
                          )}
                        </div>
                        <div>
                          <div className="block-info">{event.startDateTime}</div>
                          <div className="block-info">{event.finishDateTime}</div>
                        </div>
                      </div>
                    ) : null}

                    <div className="event-info-group">
                      <span className="block-help">Duration:</span>{' '}
                      <span className="block-info">{Event.getDurationFromSec(event.duration)}</span>
                    </div>
                  </div>

                  <div>
                    {event.pilotID && users[event.pilotID] ? (
                      <div className="event-info-group">
                        <div className="block-help no-padding">Pilot Name:</div>
                        <div className="block-info">
                          {accessgroups.userReview ? (
                            <a
                              href={`${window.serverData._uri}/org/${orgEvent.unique}/users/review/${event.pilotID}/`}
                            >
                              {users[event.pilotID].name} {users[event.pilotID].surname}{' '}
                              <span className="icon external-link" />
                            </a>
                          ) : event.pilotID === user.id ? (
                            <a href={`${window.serverData._uri}/users/review/${event.pilotID}/`}>
                              {users[event.pilotID].name} {users[event.pilotID].surname}{' '}
                              <span className="icon external-link" />
                            </a>
                          ) : (
                            <span>
                              {users[event.pilotID].name} {users[event.pilotID].surname}{' '}
                            </span>
                          )}
                        </div>
                      </div>
                    ) : null}

                    {event.secondInComandID && users[event.secondInComandID] ? (
                      <div className="event-info-group">
                        <div className="block-help">SIC Pilot Name:</div>
                        <div className="block-info">
                          {accessgroups.userReview ? (
                            <a
                              href={`${window.serverData._uri}/org/${orgEvent.unique}/users/review/${event.secondInComandID}/`}
                            >
                              {users[event.secondInComandID].name}{' '}
                              {users[event.secondInComandID].surname}{' '}
                              <span className="icon external-link" />
                            </a>
                          ) : (
                            <span>
                              {users[event.secondInComandID].name}{' '}
                              {users[event.secondInComandID].surname}{' '}
                            </span>
                          )}
                        </div>
                      </div>
                    ) : null}

                    {event.instructorUserID && users[event.instructorUserID] ? (
                      <div className="event-info-group">
                        <div className="block-help">Instructor name:</div>
                        <div className="block-info">
                          {accessgroups.userReview ? (
                            <a
                              href={`${window.serverData._uri}/org/${orgEvent.unique}/users/review/${event.instructorUserID}/`}
                            >
                              {users[event.instructorUserID].name}{' '}
                              {users[event.instructorUserID].surname}{' '}
                              <span className="icon external-link" />
                            </a>
                          ) : (
                            <span>
                              {users[event.instructorUserID].name}{' '}
                              {users[event.instructorUserID].surname}{' '}
                            </span>
                          )}
                        </div>
                      </div>
                    ) : null}

                    {event.customFields && event.customFields.length ? (
                      <div className="event-info-group event-info-group-flex-statrt">
                        <div className="block-help">Custom Fields</div>
                        <div>
                          {event.customFields.map(field => (
                            <div key={field.id} className="block-info">
                              {field.name}:{' '}
                              {field.value && field.value.length ? field.value : 'None'}
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : null}

                    {event.counters && event.counters.length ? (
                      <div className="event-info-group event-info-group-flex-statrt">
                        <div className="block-help">Meters</div>
                        <div>
                          {event.counters.map(counter => (
                            <div key={counter.id} className="block-info">
                              {counter.name}: {counter.diff}
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : null}
                  </div>
                </div>
              ) : (
                <div className="event-info">
                  <div className="event-info-group-block">
                    <div className="event-info-group">
                      <div className="block-help no-padding">Organization:</div>
                      <div className="block-info">{organization.name}</div>
                    </div>

                    {invoice.createdDateTime ? (
                      <div className="event-info-group">
                        <div className="block-help">Invoice Date:</div>
                        <div className="block-info">
                          {formatDate(invoice.userCreateTimestamp, 0, 'd M Y')}
                        </div>
                      </div>
                    ) : null}
                  </div>

                  <div>
                    {payer.id ? (
                      <div className="event-info-group">
                        <div className="block-help no-padding">Pilot Name:</div>
                        <div className="block-info">
                          {accessgroups.userReview ? (
                            <a
                              href={`${window.serverData._uri}/org/${organization.unique}/users/review/${payer.id}/`}
                            >
                              {payer.name} {payer.surname} <span className="icon external-link" />
                            </a>
                          ) : payer.id === user.id ? (
                            <a href={`${window.serverData._uri}/users/review/${payer.id}/`}>
                              {payer.name} {payer.surname} <span className="icon external-link" />
                            </a>
                          ) : (
                            <span>
                              {payer.name} {payer.surname}{' '}
                            </span>
                          )}
                        </div>
                      </div>
                    ) : null}
                  </div>
                </div>
              )}

              {view && Organization.orgAccess ? (
                <span className="delete-btn-group">
                  <button
                    type="button"
                    disabled={progress}
                    onClick={this.onDeleteInvoice}
                    className="btn btn-danger delete-btn"
                  >
                    Delete Invoice
                  </button>
                  <span className="delete-hint">Irreversible action</span>
                </span>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    );
  }
}

const modalProductSelector = selectUIModal(ADD_PRODUCT);
const modalResourceSelector = selectUIModal(ADD_RESOURCE);
const messagesSelector = selectUIMessages();
const progressSelector = selectUIProgress();

const mapStateToProps: MapStateToProps<ReduxState, OwnProps, MappedState> = state => ({
  URL: state.URL,
  addProduct: modalProductSelector(state),
  addResource: modalResourceSelector(state),
  messages: messagesSelector(state),
  progress: progressSelector(state),
  invoice: invoiceSelector(state),
  event: eventSelector(state),
  invoiceResources: invoiceResourcesSelector(state),
  invoiceResourcesList: invoiceResourcesListSelector(state),
  invoiceProducts: invoiceProductsSelector(state),
  invoiceProductsList: invoiceProductsListSelector(state),
  invoiceProductsOptions: invoiceProductsOptionsSelector(state),
  invoiceResourceOptions: invoiceResourceOptionsSelector(state),
  products: productsSelector(state),
  resources: resourcesSelector(state),
  users: usersSelector(state),
  orgEvent: organizationByEventSelector(state),
  organization: organizationSelector(state),
  organizations: organizationsSelector(state),
  transactions: transactionsSelector(state),
  transactionsList: transactionsListSelector(state),
  payer: payerSelector(state),
  user: userSelector(state),
  accessgroups: state.accessgroups
});

export default connect(
  mapStateToProps,
  {
    ...formActions,
    deleteProduct,
    deleteResource,
    deleteCounter,
    getPrice,
    addInvoice,
    saveInvoice,
    deleteInvoice,
    setModal,
    setMessages
  },
  mergeActionsToProps
)(InvoiceScreen);
